import React from 'react';
import { MultipleChoiceOption, NaOption, Icon, iconTextDictionary } from "Components";
import { useDataContext } from "UIDataHandler";
import { useKeyMap } from "KeyboardEventHelper";
import IconTypeEnum from "Enum/IconTypeEnum";

const MultipleChoice = ({ questionData, className='' }) => {
    const { currentQuestionIndex, form, setForm, goToNextQuestion, controllerMode } = useDataContext();

    const [selectedIdx, setSelectedIdx] = React.useState(-1);

    const isIcons = questionData.choices.some(choice => choice.startsWith(':') && choice.endsWith(':'));
    const hasRectangle = !isIcons && questionData.choices.some(choice => choice.length > 12);

    React.useEffect(() => {
        const currentAnswer = form.answers?.[currentQuestionIndex]?.answer;
        if (currentAnswer) {
            setSelectedIdx(questionData.choices.indexOf(currentAnswer));
        } else {
            setSelectedIdx(-1);
        }
    }, [currentQuestionIndex, questionData.choices, form.answers]);

    const select = (idx) => {
        let newSelectedIdx = controllerMode ? idx : (idx === selectedIdx ? -1 : idx);
        setSelectedIdx(newSelectedIdx);

        setForm(previousFormData => {
            const updatedAnswers = [...previousFormData.answers];
            updatedAnswers[currentQuestionIndex] = {
                ...updatedAnswers[currentQuestionIndex],
                answer: newSelectedIdx <= -1 ? '' : questionData.choices[newSelectedIdx],
                question: questionData.id,
                isNs: false,
                isNa: newSelectedIdx === -2
            }
            return {
                ...previousFormData,
                answers: updatedAnswers,
            }
        });

        if (controllerMode && newSelectedIdx !== -1) {
            goToNextQuestion();
        }
    };

    const getChoiceTextColorClass = (selected, isHover) => {
        return selected ? 'color1-text' : isHover ? `color3-text` : 'color2-text';
    }

    const convertIndex = (index, reversed = false, totalCount) => {
        return reversed ? totalCount - index : index;
    }

    const renderChoice = (choice, i , totalChoicesCount) => {
        let cardStyle = `w-[120px] h-[120px] md:w-[135px] md:h-[135px] justify-evenly`;
        if (isIcons) {
            cardStyle = `w-full justify-start md:flex-col md:w-[135px] md:h-[135px] md:justify-evenly md:text-center`;
        } else if (hasRectangle) {
            cardStyle = `w-full justify-start text-left`;
        }
        const selected = i === selectedIdx;

        return <MultipleChoiceOption
            key={`${questionData.id}-mc-${i}`}
            className={`flex-initial items-center border-2 ${cardStyle} ${selected ? `color1-bg color1-border color1-text choice-selected` : `color2-bg color2-border color2-text choice-normal`}`}
            selected={selected}
            onClick={() => select(i)}
        >
            {(isHover) => (
                <>
                    {controllerMode && <Icon className={`number-icon`} type={IconTypeEnum.NUM + convertIndex(i, controllerMode, totalChoicesCount)} size={25}/>}
                    {isIcons
                        ? <div className={`flex flex-row items-center md:flex-col md:gap-[10px]`}>
                            <Icon
                                type={choice.slice(1, -1)}
                                selected={i === selectedIdx}
                                className={`icon h-[35px] w-[35px] md:h-[45px] md:w-[45px] flex-none md:mx-auto md:mb-2 ${getChoiceTextColorClass(selected, isHover)}`}
                            />
                            <div className={`icon-text flex-none mx-auto px-[50px] md:px-0 ${getChoiceTextColorClass(selected, isHover)}`}>{iconTextDictionary[choice.slice(1, -1)] || ''}</div>
                        </div>
                        : <div className={getChoiceTextColorClass(selected, isHover)}>{choice}</div>}
                </>
            )}
        </MultipleChoiceOption>;
    };

    let keyMap = {};
    if (controllerMode) {
        questionData.choices.forEach((choice, i) => {
            keyMap[i + 1] = () => {
                select(convertIndex(i + 1, controllerMode, questionData.choices.length));
            }
        })
        if (questionData.includeNa) {
            keyMap['ArrowDown'] = () => {
                select(-2);
            }
        }
    }
    useKeyMap(keyMap);

    return <React.Fragment>
        <div
            className={
                `m-auto gap-[10px]
                ${isIcons 
                    ? `w-11/12 flex flex-col md:flex-row md:w-fit` 
                    : `w-fit 
                       ${hasRectangle 
                            ? 'grid grid-cols-1 md:grid-cols-2' 
                            : `grid grid-cols-2 md:flex md:flex-row md:justify-evenly`
                       }`
                }
                ${className}`
            }
        >
            {questionData.choices.map((choice, i) => renderChoice(choice, i, questionData.choices.length))}
        </div>
        {questionData.includeNa
            ? <NaOption onClick={() => select(-2)} isSelected={form.answers[currentQuestionIndex].isNa} />
            : null
        }
        </React.Fragment>;
};

export default MultipleChoice;
